import dayjs from "dayjs";

export function getCsrfFieldName() {
  const el = document.querySelector(`meta[name='_csrf_header']`);
  if (el) {
    return el.getAttribute("content") || "";
  }
  return "";
}

export function getCsrfValue() {
  const el = document.querySelector(`meta[name='_csrf']`);
  if (el) {
    return el.getAttribute("content") || "";
  }
  return "";
}

export function getCsrfFormParamName() {
  const el = document.querySelector(`meta[name='_csrf_parameter']`);
  if (el) {
    return el.getAttribute("content") || "";
  }
  return "";
}

export function fillCsrfParam(params: URLSearchParams) {
  const key = getCsrfFieldName();
  const value = getCsrfValue();
  if (key && value) {
    params.set(key, value);
  }
}

function isInputField(el: Element | null): el is HTMLInputElement {
  return !!el && "type" in el;
}

function isFormField(el: Element | null): el is HTMLFormElement {
  return !!el && "submit" in el;
}

export function fillForm(form: Element | null) {
  if (!isFormField(form)) {
    console.warn("Element is not form");
    return;
  }
  const el = form.querySelector("[data-type=csrf]");
  if (isInputField(el)) {
    el.name = getCsrfFieldName();
    el.value = getCsrfValue();
  }
}

export function submitForm(form: Element | null) {
  fillForm(form);
  if (isFormField(form)) {
    form.submit();
  }
}

export function format(date: Date, formatString = "YYYY-MM-DD") {
  return dayjs(date).format(formatString);
}

export function formatNullable(date: Date | undefined | null, formatString = "YYYY-MM-DD") {
  return date ? format(date, formatString) : "";
}

export function getDate(val: null): undefined;
export function getDate(val: string | number): Date;
export function getDate(val: string | null): Date | undefined;
export function getDate(val: number | null): Date | undefined;
export function getDate(val: string | number | null): Date | undefined {
  if (!val) {
    return undefined;
  }

  if (typeof val === "number") {
    return new Date(val);
  }

  return dayjs(val, "YYYY-MM-DD").toDate();
}

export function submitAsForm(
  data: Dict<string | number | boolean | undefined>,
  action: string,
  method: string = "POST",
) {
  const form = document.createElement("form");
  form.method = method;
  form.action = action;
  form.hidden = true;
  for (const [key, value] of Object.entries(data)) {
    if (!value) {
      continue;
    }

    const input = document.createElement("input");
    input.type = "hidden";
    input.name = key;
    input.value = value.toString();
    form.appendChild(input);
  }
  // CSRF token
  const input = document.createElement("input");
  input.type = "hidden";
  input.name = getCsrfFieldName();
  input.value = getCsrfValue();
  form.appendChild(input);

  document.body.appendChild(form);
  form.submit();
}

export function downloadBlob(filename: string) {
  return (blob: Blob) => {
    if (navigator.appVersion.toString().indexOf(".NET") > 0) {
      // @ts-ignore
      window.navigator.msSaveBlob(blob, filename);
    } else {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
    }
  };
}

export async function downloadBlobFromResponse(response: Response) {
  const filename = response.headers.get("Content-Disposition")!.split("filename=")[1].replace(/"/g, "");
  const blob = await response.blob();
  downloadBlob(filename)(blob);
}

export const downloadFile = (url: string): Promise<unknown> => {
  return fetch(url, {
    credentials: "same-origin", // include, omit
    mode: "cors", // no-cors, same-origin
    referrer: "no-referrer", // client
  }).then(downloadBlobFromResponse);
};

export const downloadAssetFile = async (url: string, fileName?: string) => {
  const response = await fetch(url, {
    credentials: "same-origin", // include, omit
    mode: "cors", // no-cors, same-origin
    referrer: "no-referrer", // client
  });

  if (!response.ok) {
    throw new Error(`Download failed: ${response.statusText}`);
  }

  const parts = new URL(url).pathname.split("/");
  const filename = fileName || parts[parts.length - 1];

  const blob = await response.blob();
  downloadBlob(sanitizeFilename(filename))(blob);
};

function sanitizeFilename(name: string) {
  return name.replace(/[^a-zA-Z0-9\s]/g, "_");
}
